/**
 * useLiveRoom — la salle d'un live, branchée : le SDK LiveKit, le relevé du serveur, le canal de
 * données, les onglets. La partie commune du live : rien n'y est propre à l'élève.
 *
 * Ce que le crochet fait, et d'où il le tient :
 *  - **la connexion** : l'état du SDK (`ConnectionStateChanged`, la raison d'un `Disconnected`),
 *    traduit en événements de `roomMachine`. Elle ne compte que pour la salle de cette séance : tant
 *    que la salle globale de l'app est encore sur une autre (la messagerie y écrit aussi son jeton),
 *    elle est « en connexion ». La reprise reste au SDK ; sur le web, le dernier recours
 *    `useLiveKitWebReconnect` (6/15/60 s, jamais pendant une reprise) ;
 *  - **le relevé** (`GET api/v2/live-sessions/{id}`) : au montage, à chaque reprise, puis à la
 *    fréquence que dit la machine (`pollDelay`). Il porte le jeton de la salle : la même règle que
 *    `LiveMainLayout.successCallback` (le jeton posé, la salle changée quand le lien diffère) ;
 *  - **chaque coupure mesurée** : `startOutage` au début, `end()` à la reprise, et une ligne
 *    `auto_reconnect` au journal de la séance (`LiveEventBuffer`), avec sa durée (`duration_ms`) ;
 *    une coupure jamais refermée (la sortie, la fin, l'écran démonté) s'écrit aussi, en
 *    `outage_<genre>_unrecovered` (`closeOutage`). Une reprise qui piétine puis se coupe (le SDK, ou
 *    la relance au plafond) garde son genre d'ouverture devant celui de sa fin,
 *    `outage_reconnecting_disconnected` : la mesure du pilote la compte comme une reprise qui n'a pas
 *    abouti, à côté de l'ancien live, et la distingue d'une salle coupée d'emblée ;
 *  - **le canal de données** : les messages reçus décodés et remontés (`dataChannel`), passés à
 *    `onData` ; l'émetteur (`roomSender`) n'envoie qu'en connecté ;
 *  - **les attributs** des participants : chaque changement passé à `onAttributesChanged` ;
 *  - **les onglets** (`useTabManagement`) : un autre onglet de la même séance se voit.
 *
 * Ce qu'il ne fait pas, et laisse au lecteur : rejoindre et quitter l'appel (le serveur), la
 * session audio et CallKeep du natif, ce qu'une reprise doit relire (`onResync`), ce qu'une sortie
 * veut dire (`onExit`).
 */
import { useParticipants, useRoomContext } from "@livekit/components-react"
import NetInfo from "@react-native-community/netinfo"
import {
    ConnectionState,
    DisconnectReason,
    RoomEvent,
    type Participant,
    type RemoteParticipant,
    type Room,
} from "livekit-client"
import { useCallback, useEffect, useMemo, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"

import { setLiveKitToken } from "../../../data/redux/actions/liveKitRoom"
import LiveEventBuffer from "../../../data/services/LiveEventBuffer"
import { liveServiceV2 } from "../../../data/services/liveServiceV2"
import { apiCall, ApiCallError, apiFailureOf } from "../../shared/apiCall"
import { IS_WEB } from "../../shared/platform"
import { createTelemetry, type EngineTelemetry, type OutageHandle, type TelemetryRole } from "../../shared/telemetry"
import { useSignalReducer } from "../../shared/useSignalReducer"
import { bytesToString, createChunkAssembler, type LiveDataEvent, userIdOfIdentity } from "./dataChannel"
import {
    initialRoomState,
    pollDelay,
    roomReducer,
    snapshotOf,
    stallDeadline,
    type RoomEvent as RoomMachineEvent,
    type RoomExit,
    type RoomSignal,
    type RoomState,
    type SdkConnectionState,
} from "./roomMachine"
import { CONNECT_OPTIONS } from "./roomOptions"
import { createRoomSender, type RoomSender, type SendPolicy, type SendResult } from "./roomSender"
import { useLiveKitWebReconnect } from "./useLiveKitWebReconnect"
import { useTabManagement } from "./useTabManagement"

/**
 * Un participant, sans rien de propre à un rôle : qui, ses attributs, sa caméra, son micro. Ni sa voix
 * ni la qualité de sa connexion : l'écran n'en montre rien (le grand d'un tuteur sur deux appareils ne
 * suit pas la voix, `livePeople`), et la liste ne se relit pas sur elles (`PARTICIPANT_EVENTS`).
 */
export type RoomParticipant = {
    /** L'id d'utilisateur (le préfixe de l'identité LiveKit). */
    id: string
    identity: string
    isLocal: boolean
    attributes: Record<string, string>
    cameraOn: boolean
    micOn: boolean
}

/**
 * Les événements qui relisent la liste des participants : ceux qui changent ce qu'elle porte (ses
 * attributs, une caméra ou un micro publiés, coupés, rouverts). L'arrivée, le départ et l'état de la
 * connexion la relisent toujours (le SDK les ajoute). Pas la voix : par défaut, chaque
 * `ActiveSpeakersChanged` (une prise de parole, plusieurs fois par phrase) redessinait tout l'écran du
 * live pour rien.
 */
export const PARTICIPANT_EVENTS: RoomEvent[] = [
    RoomEvent.ParticipantAttributesChanged,
    RoomEvent.TrackPublished,
    RoomEvent.TrackUnpublished,
    RoomEvent.TrackMuted,
    RoomEvent.TrackUnmuted,
    RoomEvent.LocalTrackPublished,
    RoomEvent.LocalTrackUnpublished,
]

/** Le relevé tel que le serveur le sert (`data` de `GET api/v2/live-sessions/{id}`). */
export type RoomSnapshotPayload = {
    livekit_token?: string | null
    live_session?: { conference_link?: string | null } | null
    today_occurrence?: unknown
} & Record<string, unknown>

export type UseLiveRoomOptions = {
    liveSessionId: number | null
    role: TelemetryRole
    /** Un événement du canal, et qui l'envoie (son id d'utilisateur). */
    onData?: (event: LiveDataEvent, senderId: string | null) => void
    /** Les attributs d'un participant ont changé : ceux qui changent, et lui. */
    onAttributesChanged?: (
        changed: Record<string, string>,
        participant: { id: string | null; isLocal: boolean }
    ) => void
    /**
     * Le relevé lu, tel quel (le lecteur y prend ce qui le regarde), et l'heure où il a été DEMANDÉ :
     * une lecture ne décrit que ce qui précède sa demande.
     */
    onSnapshot?: (payload: RoomSnapshotPayload, requestedAt: number) => void
    /** Une reprise : relire ce que la coupure a pu faire perdre. */
    onResync?: () => void
    /** Le relevé dit de sortir (403, 402, 404), avec le message que le serveur a écrit, s'il en a un. */
    onExit?: (exit: RoomExit, serverMessage: string | null) => void
    /**
     * Le plafond d'une reprise du SDK, réseau présent. Au-delà, la salle est déclarée bloquée : elle
     * est relancée (coupée, puis reconnectée), une fois par plafond écoulé, et « Réessayer » en fait
     * autant. Sans plafond (le défaut), une reprise du SDK n'est jamais touchée.
     */
    reconnectCeilingMs?: number
    /**
     * La salle attend : le relevé est lu, mais son jeton n'est posé (ce qui la connecte et la fait publier)
     * qu'à la levée. Le tuteur au natif, tant que ses questions de permissions n'ont pas reçu de réponse.
     */
    holdConnection?: boolean
    /** Les tests passent la leur. */
    telemetry?: EngineTelemetry
}

export type LiveRoom = {
    state: RoomState
    room: Room | undefined
    participants: RoomParticipant[]
    send: (type: string, data: Record<string, unknown>, to: number[] | null, policy: SendPolicy) => Promise<SendResult>
    /** Ferme la coupure en cours sans reprise, et l'écrit avec sa durée (à appeler avant de sortir). */
    closeOutage: () => void
    /** L'autre onglet : « Garder cet onglet », et la fenêtre à montrer. */
    tabs: { showKeepTabModal: boolean; keepThisTab: () => void }
    /** « Réessayer » : sur le web quand la salle reste coupée, et partout où le plafond est passé. */
    webReconnect: { onRetry?: () => void }
    /** Redemande un jeton : relit le relevé, qui en apporte un neuf (`currentToken`). */
    renewToken: () => Promise<void>
    /**
     * Le jeton à présenter à la salle au moment d'un `connect` : le dernier du relevé, qui en apporte un neuf à
     * chaque lecture, sinon celui du store. Celui du store, posé à l'entrée, n'est remplacé qu'au changement de
     * salle : un `connect` qui le lisait des heures plus tard le présentait expiré (401).
     */
    currentToken: () => string | null
}

/** Au-delà, les coupures d'une séance ne s'écrivent plus au journal : la dernière ligne le dit. */
export const OUTAGE_LOG_BUDGET = 30

type RoomData = { token?: string | null; roomName?: string | null; serverUrl?: string | null }
type RootState = { liveKitRoom?: { roomData?: RoomData } }

function sdkStateOf(state: ConnectionState | string): SdkConnectionState {
    switch (state) {
        case ConnectionState.Connected:
            return "connected"
        case ConnectionState.Connecting:
            return "connecting"
        case ConnectionState.Reconnecting:
            return "reconnecting"
        case ConnectionState.SignalReconnecting:
            return "signalReconnecting"
        default:
            return "disconnected"
    }
}

function toParticipant(participant: Participant): RoomParticipant {
    return {
        id: userIdOfIdentity(participant.identity) ?? participant.identity,
        identity: participant.identity,
        isLocal: participant.isLocal,
        attributes: { ...(participant.attributes ?? {}) },
        cameraOn: participant.isCameraEnabled,
        micOn: participant.isMicrophoneEnabled,
    }
}

/** Le nom de la salle à laquelle le SDK est joint (`roomInfo`, que `disconnect()` ne vide pas). */
function joinedRoomName(room: Room | undefined): string | null {
    const info = (room as unknown as { roomInfo?: { name?: string } } | undefined)?.roomInfo
    return info?.name ?? null
}

export function useLiveRoom(options: UseLiveRoomOptions): LiveRoom {
    const { liveSessionId, role } = options
    const room = useRoomContext() as Room | undefined
    const dispatch = useDispatch()
    const roomData = useSelector((state: RootState) => state.liveKitRoom?.roomData)
    const roomDataRef = useRef(roomData)
    roomDataRef.current = roomData
    const freshTokenRef = useRef<string | null>(null)
    const currentToken = useCallback(() => freshTokenRef.current ?? roomDataRef.current?.token ?? null, [])

    const telemetry = useMemo(
        () => options.telemetry ?? createTelemetry("live-room", { role }),
        [options.telemetry, role]
    )
    const callbacks = useRef(options)
    callbacks.current = options

    // La salle de CETTE séance, telle que le relevé la nomme. Avant, la salle globale peut encore être
    // sur une autre (la messagerie) : on ne la compte pas comme connectée.
    const expectedRoomRef = useRef<string | null>(null)
    const outageRef = useRef<OutageHandle | null>(null)
    const outageClosedRef = useRef(false)
    /** Le genre de la coupure en cours à son ouverture : la machine le change quand une reprise se coupe. */
    const outageOpenedAsRef = useRef<string | null>(null)
    /** Le message du dernier relevé refusé : la sortie le dit (le paiement en attente, le jour du live). */
    const exitMessageRef = useRef<string | null>(null)
    const outageLinesRef = useRef(0)
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const mountedRef = useRef(true)
    const senderRef = useRef<RoomSender | null>(null)
    const readSnapshotRef = useRef<() => void>(() => {})
    const reportRef = useRef<() => void>(() => {})

    const onSignals = useCallback(
        (signals: RoomSignal[]) => {
            for (const signal of signals) {
                switch (signal.type) {
                    case "transition":
                        telemetry.transition(signal.from, signal.to, signal.event)
                        if (signal.to === "connected") void senderRef.current?.flush()
                        break
                    case "outage_started":
                        outageRef.current = telemetry.startOutage(signal.kind)
                        outageClosedRef.current = false
                        outageOpenedAsRef.current = signal.kind
                        break
                    case "outage_ended": {
                        // Déjà écrite, fermée par la sortie ou la fin : la reprise qui suit ne compte pas deux fois.
                        if (outageClosedRef.current) {
                            outageClosedRef.current = false
                            break
                        }
                        const measure = outageRef.current?.end() ?? null
                        outageRef.current = null
                        const durationMs = measure?.durationMs ?? signal.durationMs
                        recordOutage(outageLine(outageOpenedAsRef.current, signal.kind), durationMs, outageLinesRef)
                        break
                    }
                    case "read_snapshot":
                        readSnapshotRef.current()
                        break
                    case "resync":
                        callbacks.current.onResync?.()
                        break
                    case "exit":
                        telemetry.transition("in_live", `exit:${signal.exit}`, "snapshot", { status: signal.status })
                        callbacks.current.onExit?.(signal.exit, exitMessageRef.current)
                        break
                    case "snapshot_failed":
                        // Hors ligne : une trace. Une erreur serveur : une erreur, avec son code.
                        if (signal.offline || signal.status == null) {
                            telemetry.transition("snapshot", "snapshot_failed", "offline", {
                                failures: signal.failures,
                            })
                        } else {
                            telemetry.error("snapshot", new Error(`HTTP ${signal.status}`), {
                                status: signal.status,
                                failures: signal.failures,
                            })
                        }
                        break
                    case "stalled":
                        telemetry.transition(signal.connection, "stalled", "ceiling", { stuckMs: signal.stuckMs })
                        // Une fois par coupure à Bugsnag : c'est la reprise du SDK qui n'aboutit pas.
                        if (signal.first) {
                            telemetry.error("reconnect_stalled", new Error("la reprise de la salle n'aboutit pas"), {
                                connection: signal.connection,
                                stuckMs: signal.stuckMs,
                            })
                        }
                        relaunchRef.current("auto")
                        break
                }
            }
        },
        [telemetry]
    )

    const [state, dispatchRoom, read] = useSignalReducer(roomReducer, () => initialRoomState, onSignals)

    /*
     * Le plafond d'une reprise (`reconnectCeilingMs`) : une minuterie armée sur l'échéance que dit la
     * machine, réarmée à chaque événement qui la déplace. L'état vit dans une référence : elle se règle
     * au moment même de l'événement, sans attendre le rendu.
     */
    const ceilingRef = useRef(options.reconnectCeilingMs)
    ceilingRef.current = options.reconnectCeilingMs
    const stallTimerRef = useRef<{ deadline: number; timer: ReturnType<typeof setTimeout> } | null>(null)
    const sendRef = useRef<(event: RoomMachineEvent) => void>(() => {})
    const syncStallTimer = useCallback(() => {
        const ceiling = ceilingRef.current
        const deadline = ceiling == null || !mountedRef.current ? null : stallDeadline(read(), ceiling)
        const armed = stallTimerRef.current
        if (armed?.deadline === deadline) return
        if (armed != null) clearTimeout(armed.timer)
        stallTimerRef.current =
            deadline == null || ceiling == null
                ? null
                : {
                      deadline,
                      timer: setTimeout(
                          () => {
                              stallTimerRef.current = null
                              sendRef.current({ type: "STALL_CHECK", at: Date.now(), ceilingMs: ceiling })
                          },
                          Math.max(0, deadline - Date.now())
                      ),
                  }
    }, [read])
    const send = useCallback(
        (event: RoomMachineEvent) => {
            dispatchRoom(event)
            syncStallTimer()
        },
        [dispatchRoom, syncStallTimer]
    )
    sendRef.current = send
    useEffect(
        () => () => {
            if (stallTimerRef.current != null) clearTimeout(stallTimerRef.current.timer)
            stallTimerRef.current = null
        },
        []
    )

    /*
     * Relancer la salle : la couper, puis la reconnecter — le geste de `useLiveKitWebReconnect` sur une
     * salle coupée, précédé de la coupure, car une reprise du SDK bloquée ne rend jamais la main. La
     * coupure annule aussi une connexion restée en vol. Une seule coupure à la fois ; la connexion, elle,
     * peut piétiner : la relance suivante la coupera.
     */
    const relaunchingRef = useRef(false)
    const relaunch = useCallback(
        (trigger: "auto" | "manual") => {
            if (room == null || relaunchingRef.current || !currentToken()) return
            relaunchingRef.current = true
            telemetry.transition(read().connection, "relaunch", trigger)
            room.disconnect(true)
                .then(() => {
                    relaunchingRef.current = false
                    // L'élève est sorti pendant la coupure : on ne rouvre pas sa salle.
                    const token = currentToken()
                    if (!mountedRef.current || !token) return undefined
                    return room.connect(roomDataRef.current?.serverUrl ?? "", token, CONNECT_OPTIONS)
                })
                .catch((cause: unknown) => {
                    relaunchingRef.current = false
                    // Attendu : le réseau toujours absent, ou la relance suivante qui coupe celle-ci.
                    telemetry.transition("relaunch", "relaunch_failed", trigger, {
                        message: cause instanceof Error ? cause.message : String(cause),
                    })
                })
        },
        [room, read, telemetry, currentToken]
    )
    const relaunchRef = useRef(relaunch)
    relaunchRef.current = relaunch
    const retryStalled = useCallback(() => relaunchRef.current("manual"), [])

    // Le réseau de l'appareil : hors ligne, le plafond ne court pas. Seulement chez qui pose un plafond.
    const watchNetwork = options.reconnectCeilingMs != null
    useEffect(() => {
        if (!watchNetwork) return undefined
        const onNetwork = (online: boolean) => send({ type: "NETWORK", online, at: Date.now() })
        if (!IS_WEB) {
            // Le natif n'a pas les événements du navigateur : NetInfo dit le réseau de l'appareil.
            return NetInfo.addEventListener((network) => onNetwork(network.isConnected !== false))
        }
        if (typeof window === "undefined" || typeof window.addEventListener !== "function") return undefined
        const onOnline = () => onNetwork(true)
        const onOffline = () => onNetwork(false)
        if (typeof navigator !== "undefined" && navigator.onLine === false) onOffline()
        window.addEventListener("online", onOnline)
        window.addEventListener("offline", onOffline)
        return () => {
            window.removeEventListener("online", onOnline)
            window.removeEventListener("offline", onOffline)
        }
    }, [watchNetwork, send])

    /*
     * La coupure en cours, fermée sans reprise : l'élève sort, la séance finit, l'écran se démonte.
     * Elle s'écrit comme les autres, avec sa durée, avant que la sortie ne vide le journal de la séance.
     */
    const closeOutage = useCallback(() => {
        const handle = outageRef.current
        const kind = read().outage?.kind
        if (handle == null || kind == null || outageClosedRef.current) return
        outageClosedRef.current = true
        outageRef.current = null
        const measure = handle.end({ recovered: false })
        recordOutage(`${outageLine(outageOpenedAsRef.current, kind)}_unrecovered`, measure.durationMs, outageLinesRef)
    }, [read])
    const closeOutageRef = useRef(closeOutage)
    closeOutageRef.current = closeOutage
    useEffect(() => () => closeOutageRef.current(), [])

    /* ── l'état du SDK ── */
    useEffect(() => {
        if (room == null) return undefined
        let duplicateIdentity = false
        const report = () => {
            const sdk = sdkStateOf(room.state)
            // Joint à une autre salle (la messagerie), ou la nôtre pas encore nommée : pas encore connectée.
            const ours = expectedRoomRef.current != null && joinedRoomName(room) === expectedRoomRef.current
            const effective: SdkConnectionState = sdk === "connected" && !ours ? "connecting" : sdk
            send({ type: "SDK_STATE", state: effective, at: Date.now(), duplicateIdentity })
        }
        const onDisconnected = (reason?: DisconnectReason) => {
            duplicateIdentity = reason === DisconnectReason.DUPLICATE_IDENTITY
            report()
            duplicateIdentity = false
        }
        reportRef.current = report
        report()
        room.on(RoomEvent.ConnectionStateChanged, report)
        room.on(RoomEvent.Disconnected, onDisconnected)
        return () => {
            room.off(RoomEvent.ConnectionStateChanged, report)
            room.off(RoomEvent.Disconnected, onDisconnected)
            reportRef.current = () => {}
        }
    }, [room, send])

    /* ── le relevé ── */
    const holdRef = useRef(options.holdConnection === true)
    holdRef.current = options.holdConnection === true
    const heldPayloadRef = useRef<RoomSnapshotPayload | null>(null)
    const applyToken = useCallback(
        (payload: RoomSnapshotPayload) => {
            // `LiveMainLayout.successCallback` : le jeton posé quand la salle ou le lien diffèrent ; la
            // salle changée quand le SDK est joint à une autre, ou en route vers une autre. Une salle
            // jointe nulle part et à l'arrêt n'a rien à changer : le jeton posé, les pilotes de la
            // connexion (`LiveKitRoom`, et le natif) la connectent. La couper là annulait la connexion
            // qu'ils venaient de lancer, et l'élève entrait deux fois (iOS : 6 s plus tard).
            const link = payload?.live_session?.conference_link ?? null
            const token = payload?.livekit_token ?? null
            const current = roomDataRef.current
            if (link != null && expectedRoomRef.current !== link) {
                expectedRoomRef.current = link
                reportRef.current()
            }
            if (token != null) freshTokenRef.current = token
            if (holdRef.current) {
                heldPayloadRef.current = payload
                return
            }
            if (room == null || token == null) return
            const joined = joinedRoomName(room)
            if (!current?.token || joined !== link || current?.roomName !== link) {
                dispatch(setLiveKitToken(token, link) as never)
                const joinedElsewhere = joined != null && joined !== link
                const headingElsewhere =
                    joined == null && room.state !== ConnectionState.Disconnected && current?.roomName !== link
                if (joinedElsewhere || headingElsewhere) {
                    room.disconnect(true)
                        .then(() => room.connect(current?.serverUrl ?? "", token, CONNECT_OPTIONS))
                        .catch((cause: unknown) => {
                            telemetry.error("switch_room", cause)
                        })
                }
            }
        },
        [room, dispatch, telemetry]
    )

    // La levée : le dernier relevé retenu pose son jeton.
    useEffect(() => {
        if (options.holdConnection === true || heldPayloadRef.current == null) return
        const held = heldPayloadRef.current
        heldPayloadRef.current = null
        applyToken(held)
    }, [options.holdConnection, applyToken])

    const schedulePoll = useCallback(() => {
        if (pollTimerRef.current != null) clearTimeout(pollTimerRef.current)
        const delay = pollDelay(read())
        if (delay == null || !mountedRef.current) return
        pollTimerRef.current = setTimeout(() => readSnapshotRef.current(), delay)
    }, [read])

    const readSnapshot = useCallback((): Promise<void> => {
        if (liveSessionId == null) return Promise.resolve()
        if (pollTimerRef.current != null) clearTimeout(pollTimerRef.current)
        const requestedAt = Date.now()
        return apiCall<RoomSnapshotPayload>(dispatch as never, liveServiceV2.getLiveSessionV2, {
            routeParams: { id: liveSessionId },
        })
            .then((payload) => {
                if (!mountedRef.current) return
                applyToken(payload)
                send({ type: "SNAPSHOT_READ", snapshot: snapshotOf(payload), at: Date.now() })
                callbacks.current.onSnapshot?.(payload, requestedAt)
            })
            .catch((cause: unknown) => {
                if (!mountedRef.current) return
                const status = cause instanceof ApiCallError ? cause.status : null
                const failure = apiFailureOf(cause)
                const offline = failure?.isOffline === true
                const message = failure?.message
                exitMessageRef.current = typeof message === "string" ? message : null
                send({ type: "SNAPSHOT_FAILED", status, offline, at: Date.now() })
            })
            .finally(() => {
                if (mountedRef.current) schedulePoll()
            })
    }, [liveSessionId, dispatch, applyToken, send, schedulePoll])
    readSnapshotRef.current = readSnapshot

    useEffect(() => {
        mountedRef.current = true
        readSnapshot()
        return () => {
            mountedRef.current = false
            if (pollTimerRef.current != null) clearTimeout(pollTimerRef.current)
        }
        // Une seule fois par séance : la suite, c'est la machine qui la règle.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveSessionId])

    // Une coupure fait monter la fréquence, une reprise la redescend : le prochain relevé suit l'état.
    useEffect(() => {
        if (state.outage != null) schedulePoll()
    }, [state.outage, schedulePoll])

    /* ── le canal de données et les attributs ── */
    useEffect(() => {
        if (room == null) return undefined
        const assembler = createChunkAssembler()
        const onData = (payload: Uint8Array, participant?: RemoteParticipant) => {
            let event: LiveDataEvent | null = null
            try {
                event = assembler.push(JSON.parse(bytesToString(payload)) as unknown)
            } catch (cause) {
                telemetry.error("data_decode", cause, { bytes: payload?.length ?? 0 })
                return
            }
            if (event == null) return
            callbacks.current.onData?.(event, userIdOfIdentity(participant?.identity))
        }
        const onAttributes = (changed: Record<string, string>, participant: Participant) => {
            callbacks.current.onAttributesChanged?.(changed ?? {}, {
                id: userIdOfIdentity(participant?.identity),
                isLocal: participant?.isLocal === true,
            })
        }
        room.on(RoomEvent.DataReceived, onData)
        room.on(RoomEvent.ParticipantAttributesChanged, onAttributes)
        return () => {
            room.off(RoomEvent.DataReceived, onData)
            room.off(RoomEvent.ParticipantAttributesChanged, onAttributes)
            assembler.clear()
        }
    }, [room, telemetry])

    /* ── l'émetteur ── */
    const sender = useMemo(() => {
        if (room == null) return null
        return createRoomSender({
            isConnected: () => read().connection === "connected",
            remoteIdentities: () => [...(room.remoteParticipants?.keys() ?? [])],
            publish: (bytes, identities) =>
                room.localParticipant.publishData(bytes, {
                    reliable: true,
                    ...(identities != null ? { destinationIdentities: identities } : {}),
                }),
            telemetry,
        })
    }, [room, read, telemetry])
    senderRef.current = sender
    useEffect(() => () => sender?.clear(), [sender])

    const sendData = useCallback(
        (type: string, data: Record<string, unknown>, to: number[] | null, policy: SendPolicy): Promise<SendResult> =>
            sender != null ? sender.send(type, data, to, policy) : Promise.resolve({ ok: false, reason: "dropped" }),
        [sender]
    )

    /* ── les onglets et le dernier recours du web ── */
    const tabs = useTabManagement(liveSessionId) as {
        showKeepTabModal: boolean
        handleKeepThisTab: () => void
        hasOtherTabs: boolean
    }
    useEffect(() => {
        send({ type: "OTHER_TAB", present: tabs.hasOtherTabs })
    }, [tabs.hasOtherTabs, send])
    const webReconnect = useLiveKitWebReconnect(room, roomData, tabs.hasOtherTabs) as { onRetry?: () => void }

    /* ── les participants ── */
    const rawParticipants = useParticipants({ updateOnlyOn: PARTICIPANT_EVENTS })
    const participants = useMemo(() => rawParticipants.map(toParticipant), [rawParticipants])

    return {
        state,
        room,
        participants,
        send: sendData,
        closeOutage,
        tabs: { showKeepTabModal: tabs.showKeepTabModal, keepThisTab: tabs.handleKeepThisTab },
        // Passé le plafond, « Réessayer » relance la salle, dans tout état ; sinon, celui du web sur une salle coupée.
        webReconnect: { onRetry: state.stalled ? retryStalled : webReconnect.onRetry },
        renewToken: readSnapshot,
        currentToken,
    }
}

/** Le genre écrit d'une coupure : celui de sa fin, précédé de celui de son ouverture quand il a changé. */
function outageLine(openedAs: string | null, kind: string): string {
    return openedAs != null && openedAs !== kind ? `${openedAs}_${kind}` : kind
}

/**
 * La coupure, au journal de la séance : une ligne `auto_reconnect` qui dit son genre et sa durée.
 * `events-batch` garde `duration_ms` depuis ce chantier. Un budget par séance, comme les lignes du
 * SDK (`LKEvents`) : la dernière dit que le journal est tronqué.
 */
function recordOutage(kind: string, durationMs: number, linesRef: { current: number }) {
    const used = linesRef.current
    if (used > OUTAGE_LOG_BUDGET) return
    linesRef.current = used + 1
    const buffer = LiveEventBuffer.getInstance()
    buffer.record(
        "auto_reconnect",
        used === OUTAGE_LOG_BUDGET
            ? { reason: "outage_log_capped" }
            : { reason: `outage_${kind}`, duration_ms: Math.round(durationMs) }
    )
    buffer.flush()
}
